/**
 * Connect can only be enabled on a Paystack subscription. Mirrors the server's
 * PROVIDER_MIGRATION_REQUIRED check: no subscription means nothing to migrate.
 */
export function needsPaystackMigration(subscription: { provider?: string } | null | undefined): boolean {
  if (!subscription) return false;
  return subscription.provider !== 'paystack';
}

/** Connect roles clinical can give. Owner is the practice owner's; era_processor/super_admin are platform-internal. */
export const CONNECT_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'billing_manager', label: 'Billing Manager' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'provider', label: 'Provider' },
  { value: 'viewer', label: 'Viewer' },
];

/** Names for every Connect role, including those set on Connect that clinical cannot give. */
const CONNECT_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  era_processor: 'ERA Processor',
  front_desk: 'Front Desk',
  accountant: 'Accountant',
  ...Object.fromEntries(CONNECT_ROLE_OPTIONS.map(o => [o.value, o.label])),
};

export function connectRoleLabel(role: string): string {
  return CONNECT_ROLE_LABELS[role] || role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** How long a setup may wait on Connect before the panel offers to send it again. */
export const CONNECT_SETUP_SLOW_MS = 5 * 60 * 1000;

export type ConnectPanelState = 'none' | 'pending' | 'failed' | 'provisioned';

/** Where the practice's Connect setup stands. A Connect tenant means it is done, whatever the status says. */
export function connectPanelState(group: any): ConnectPanelState {
  if (group?.connectTenantId) return 'provisioned';
  if (group?.connectProvisionStatus === 'failed') return 'failed';
  if (group?.connectEnabled || group?.connectProvisionStatus === 'pending') return 'pending';
  return 'none';
}

/** A setup still waiting after five minutes, or with no record of when it was asked for. */
export function connectSetupIsSlow(group: any, now: number = Date.now()): boolean {
  const requestedAt = group?.connectProvisionRequestedAt ? new Date(group.connectProvisionRequestedAt).getTime() : NaN;
  return isNaN(requestedAt) || now - requestedAt >= CONNECT_SETUP_SLOW_MS;
}

/** The steps to switch Connect on, as the panel lists them before it is enabled. */
export function connectSetupSteps(subscription: { provider?: string } | null | undefined): { label: string; detail: string; done: boolean }[] {
  return [
    { label: 'Card on Paystack', detail: 'Connect bills through Paystack.', done: !!subscription && !needsPaystackMigration(subscription) },
    { label: 'Who gets access', detail: 'Choose the members who use Connect and the role each one has.', done: false },
    { label: 'Who pays', detail: 'Your subscription card on a day you pick, or a billing bureau.', done: false },
  ];
}
